import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

export const APPS_SCRIPT_SETUP_PATH = '/settings/connectors/tools/google-apps-script';
export const APPS_SCRIPT_SETUP_PHASES = ['OAUTH_CLIENT', 'NEED_RUNTIME_AUTH', 'READY'] as const;
export type AppsScriptSetupPhase = (typeof APPS_SCRIPT_SETUP_PHASES)[number];

export interface AppsScriptSetup {
  phase: AppsScriptSetupPhase;
  mcpConnectionId?: string;
  projectNumber?: string;
  callbackUrl?: string;
  googleSubject?: string;
  googleEmail?: string;
  grantedScopes?: string[];
  clientConfigured?: boolean;
}

export type AppsScriptSetupRequest =
  | { intent: 'status' | 'authorize-runtime' }
  | { intent: 'set-oauth-client'; clientId: string; clientSecret: string };

export function readAppsScriptSetup(value: unknown): AppsScriptSetup | null {
  if (typeof value !== 'object' || value === null || !('phase' in value)) return null;
  const phase = APPS_SCRIPT_SETUP_PHASES.find((candidate) => candidate === value.phase);
  if (!phase) return null;
  const field = (key: string) => {
    const item: unknown = Reflect.get(value, key);
    return typeof item === 'string' ? item : undefined;
  };
  return {
    phase,
    mcpConnectionId: field('mcpConnectionId'), projectNumber: field('projectNumber'),
    callbackUrl: field('callbackUrl'), googleSubject: field('googleSubject'),
    googleEmail: field('googleEmail'),
    clientConfigured: 'clientConfigured' in value && value.clientConfigured === true,
    grantedScopes: 'grantedScopes' in value && Array.isArray(value.grantedScopes)
      ? value.grantedScopes.filter((scope): scope is string => typeof scope === 'string') : [],
  };
}

export function isAppsScriptAuthorizationUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'accounts.google.com' && !url.username && !url.password && !url.port;
  } catch {
    return false;
  }
}

export async function updateAppsScriptSetup(request: AppsScriptSetupRequest, expectedMetaSiteId?: string): Promise<{ setup: AppsScriptSetup; metaSiteId: string; authorizeUrl?: string }> {
  const response = await apiFetch(`${API_BASE}/api/connectors/apps-script-setup`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...request, expectedMetaSiteId }),
  });
  if (!response.ok) throw new Error('apps_script_setup_unavailable');
  const data: unknown = await response.json();
  if (typeof data !== 'object' || data === null || !('setup' in data) || !('metaSiteId' in data) || typeof data.metaSiteId !== 'string' || !data.metaSiteId) throw new Error('invalid_apps_script_setup');
  const setup = readAppsScriptSetup(data.setup);
  if (!setup) throw new Error('invalid_apps_script_setup');
  if (expectedMetaSiteId && expectedMetaSiteId !== data.metaSiteId) throw new Error('apps_script_setup_account_changed');
  if ('authorizeUrl' in data && data.authorizeUrl) {
    if (!isAppsScriptAuthorizationUrl(data.authorizeUrl)) throw new Error('invalid_apps_script_authorization_url');
    return { setup, metaSiteId: data.metaSiteId, authorizeUrl: data.authorizeUrl };
  }
  return { setup, metaSiteId: data.metaSiteId };
}
